import { useEffect, useMemo, useState } from "react";
import {
  getWorkorderActivities,
  getWorkorderDaylog,
  getWorkorderPhotos,
} from "../../services/workorderActivityService";
import {
  createObjectUrlFromDocument,
  isSecureFileUrl,
  resolveAssetUrl,
} from "../../utils/secureFileBlobs";

const TABS = [
  { key: "activities", label: "Activiteiten" },
  { key: "daylog", label: "Daglog (alleen lezen)" },
  { key: "photos", label: "Foto's" },
  { key: "materials", label: "Materialen" },
  { key: "notes", label: "Notities" },
];

function getPhotoId(photo, fallback = "") {
  return String(photo?._id || photo?.id || fallback || "");
}

function getPhotoSourceUrl(photo) {
  return String(photo?.thumbnailUrl || photo?.fileUrl || photo?.url || "").trim();
}

export default function ProjectLeaderDaylogTabs({ workorderId, financial, legacyNotes = [] }) {
  const [activeTab, setActiveTab] = useState("daylog");
  const [activities, setActivities] = useState([]);
  const [daylog, setDaylog] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      if (!workorderId) return;
      setLoading(true);
      setError("");
      try {
        const [nextActivities, nextDaylog, nextPhotos] = await Promise.all([
          getWorkorderActivities(workorderId),
          getWorkorderDaylog(workorderId),
          getWorkorderPhotos(workorderId),
        ]);
        setActivities(nextActivities);
        setDaylog(nextDaylog);
        setPhotos(nextPhotos);
      } catch (err) {
        setError(err?.response?.data?.message || "Daglog laden mislukt");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [workorderId]);

  useEffect(() => {
    const combinedPhotos = [
      ...(Array.isArray(photos) ? photos : []),
      ...(Array.isArray(daylog)
        ? daylog.flatMap((item) => (Array.isArray(item?.photos) ? item.photos : []))
        : []),
    ];
    const securePhotos = combinedPhotos.filter((photo) =>
      isSecureFileUrl(getPhotoSourceUrl(photo))
    );
    if (securePhotos.length === 0) {
      setPhotoPreviewUrls({});
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];

    (async () => {
      const entries = await Promise.all(
        securePhotos.map(async (photo) => {
          const sourceUrl = getPhotoSourceUrl(photo);
          try {
            const objectUrl = await createObjectUrlFromDocument(sourceUrl);
            objectUrls.push(objectUrl);
            return [getPhotoId(photo), objectUrl];
          } catch {
            return [getPhotoId(photo), ""];
          }
        })
      );

      if (cancelled) {
        objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
        return;
      }

      setPhotoPreviewUrls(
        Object.fromEntries(entries.filter(([key, value]) => key && value))
      );
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [photos, daylog]);

  async function openPhoto(url) {
    try {
      const objectUrl = await createObjectUrlFromDocument(url);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      window.alert("Foto openen mislukt.");
    }
  }

  const notes = useMemo(() => {
    const primary = Array.isArray(daylog)
      ? daylog.filter((item) => String(item?.notes || "").trim())
      : [];
    const legacy = Array.isArray(legacyNotes)
      ? legacyNotes.filter((item) =>
          String(item?.notes || item?.omschrijving || item?.description || "").trim()
        )
      : [];

    return [...primary, ...legacy].filter((item, index, list) => {
      const noteText = String(
        item?.notes || item?.omschrijving || item?.description || ""
      ).trim();
      const when = String(item?.startTime || item?.datum || item?.date || "");
      const key = `${noteText}|${when}`;
      return (
        index ===
        list.findIndex((candidate) => {
          const candidateText = String(
            candidate?.notes ||
              candidate?.omschrijving ||
              candidate?.description ||
              ""
          ).trim();
          const candidateWhen = String(
            candidate?.startTime || candidate?.datum || candidate?.date || ""
          );
          return `${candidateText}|${candidateWhen}` === key;
        })
      );
    });
  }, [daylog, legacyNotes]);

  return (
    <section className="rounded-xl border bg-white p-4">
      <p className="mb-3 text-xs text-gray-600">
        Daglog wordt ingevuld door de monteur tijdens uitvoering. Hier zie je alleen de geregistreerde regels.
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`rounded px-3 py-1.5 text-sm ${
              activeTab === tab.key ? "bg-blue-700 text-white" : "border bg-white text-gray-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-gray-500">Laden...</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && activeTab === "activities" && (
        <div className="space-y-2">
          {activities.length === 0 && <p className="text-sm text-gray-500">Geen activiteiten.</p>}
          {activities.map((item) => (
            <div key={item._id} className="rounded border p-3 text-sm">
              <p className="font-medium">{item.title}</p>
              <p className="text-gray-600">Status: {item.status || "-"}</p>
            </div>
          ))}
        </div>
      )}

      {!loading && activeTab === "daylog" && (
        <div className="space-y-2">
          {daylog.length === 0 && <p className="text-sm text-gray-500">Nog geen daglogregels.</p>}
          {daylog.map((item) => (
            <div key={item.timeEntryId} className="rounded border p-3 text-sm">
              <p className="font-medium">
                {formatTime(item.startTime)} {item.technician}
              </p>
              <p>Activiteit: {item.activity || "-"}</p>
              <p>Stopreden: {item.stopReason || "-"}</p>
              {item.notes ? <p>Notitie: {item.notes}</p> : null}
              <p className="text-xs text-gray-600">
                Foto's: {Array.isArray(item.photos) ? item.photos.length : 0}
              </p>
              {Array.isArray(item.photos) && item.photos.length > 0 ? (
                <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-5">
                  {item.photos.map((photo, photoIndex) => {
                    const photoId = getPhotoId(
                      photo,
                      `${item.timeEntryId || "daylog"}-${photoIndex}`
                    );
                    const sourceUrl = getPhotoSourceUrl(photo);
                    const previewUrl =
                      photoPreviewUrls[photoId] ||
                      (!isSecureFileUrl(sourceUrl) ? resolveAssetUrl(sourceUrl) : "");

                    if (!sourceUrl) {
                      return null;
                    }

                    return (
                      <button
                        key={photoId}
                        type="button"
                        onClick={() => openPhoto(photo.fileUrl || photo.url || photo.thumbnailUrl || "")}
                        className="block text-left"
                      >
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt={photo.description || "daglog foto"}
                            className="aspect-square w-full rounded object-cover"
                          />
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center rounded bg-slate-100 text-xs text-slate-500">
                            Laden...
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {!loading && activeTab === "photos" && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {photos.length === 0 && <p className="text-sm text-gray-500">Nog geen foto's.</p>}
          {photos.map((photo) => (
            <button
              key={photo._id}
              type="button"
              onClick={() => openPhoto(photo.fileUrl || photo.url || photo.thumbnailUrl || "")}
              className="block text-left"
            >
              {photoPreviewUrls[String(photo._id || "")] ||
              !isSecureFileUrl(photo.thumbnailUrl || photo.fileUrl || photo.url || "") ? (
                <img
                  src={
                    photoPreviewUrls[String(photo._id || "")] ||
                    resolveAssetUrl(photo.thumbnailUrl || photo.fileUrl || photo.url || "")
                  }
                  alt={photo.description || "foto"}
                  className="aspect-square w-full rounded object-cover"
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center rounded bg-slate-100 text-xs text-slate-500">
                  Laden...
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && activeTab === "materials" && (
        <div className="space-y-2">
          {(financial?.materialEntries || []).length === 0 && (
            <p className="text-sm text-gray-500">Geen materiaalregels.</p>
          )}
          {(financial?.materialEntries || []).map((item, idx) => (
            <div key={`mat-${idx}`} className="rounded border p-2 text-sm">
              {item.description || "-"} | {item.qty || 0} {item.unit || "st"}
            </div>
          ))}
        </div>
      )}

      {!loading && activeTab === "notes" && (
        <div className="space-y-2">
          {notes.length === 0 && <p className="text-sm text-gray-500">Geen notities.</p>}
          {notes.map((item, idx) => (
            <div key={`note-${idx}`} className="rounded border p-2 text-sm">
              {item.notes || item?.omschrijving || "-"}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function formatTime(value) {
  if (!value) return "--:--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}
